import { useMemo } from "react";

import "ol/ol.css";

import { Fill, Icon, Stroke, Style } from "ol/style";

import { iconPath } from "utils/paths";

import type { Options as IconOptions } from "ol/style/Icon";

export const FALLBACK_COLOR = "#FFFFFF";

// This list must contain all of the colors found in tools/cloud_api/utils/colors.py
export const COLORS = [
  "#F87171",
  "#FACC15",
  "#34D399",
  "#38BDF8",
  "#A78BFA",
  "#DB2777",
  "#FB923C",
  "#65A30D",
  "#0D9488",
  "#2563EB",
  "#9333EA",
  "#FB7185",
  "#FBBF24",
  "#4ADE80",
  "#22D3EE",
  "#818CF8",
  "#E879F9",
  "#DC2626",
  "#CA8A04",
  "#059669",
  "#0284C7",
  "#7C3AED",
  "#F472B6",
  "#EA580C",
  "#A3E635",
  "#2DD4BF",
  "#60A5FA",
  "#C084FC",
  "#E11D48",
  "#D97706",
  "#16A34A",
  "#0891B2",
  "#4F46E5",
  "#C026D3",
  // use white as a fallback color, these likely won't exist, it will just be used to catch legacy tags
  FALLBACK_COLOR,
] as const;

export const hexToRgb = (hex: string) => {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const sanitized = hex.replace(shorthandRegex, function (_m, r, g, b) {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(sanitized);
  return result
    ? [parseInt(result[1]!, 16), parseInt(result[2]!, 16), parseInt(result[3]!, 16)]
    : null;
};

const IMAGE_STYLE_ARGS: IconOptions = {
  src: iconPath("annotation-tag"),
  anchor: [0, 43],
  anchorXUnits: "pixels",
  anchorYUnits: "pixels",
};

const iconStyle: IconOptions = IMAGE_STYLE_ARGS;
const iconStyleMuted: IconOptions = {
  scale: 0.9,
  opacity: 0.5,
  ...IMAGE_STYLE_ARGS,
};
const iconStyleSelected: IconOptions = {
  scale: 1.2,
  ...IMAGE_STYLE_ARGS,
};
// NOTE(Quentin): Hover and Selected currently match for points, but could diverge in the future.
const iconStyleHover: IconOptions = {
  scale: 1.2,
  ...IMAGE_STYLE_ARGS,
};

export enum MARKER_TYPES {
  normal = "normal",
  hover = "hover",
  selected = "selected",
  muted = "muted",
}

const getStyles = (hexColor: string): Record<MARKER_TYPES, Style> => {
  const color = hexToRgb(hexColor) ?? [0, 0, 0];
  return {
    [MARKER_TYPES.normal]: new Style({
      image: new Icon({
        color,
        ...iconStyle,
      }),
    }),
    [MARKER_TYPES.muted]: new Style({
      image: new Icon({
        color,
        ...iconStyleMuted,
      }),
    }),
    [MARKER_TYPES.selected]: new Style({
      image: new Icon({
        color,
        ...iconStyleSelected,
      }),
    }),
    [MARKER_TYPES.hover]: new Style({
      image: new Icon({
        color,
        ...iconStyleHover,
      }),
    }),
  };
};

const getPolygonStyles = (hexColor: string): Record<MARKER_TYPES, Style> => {
  const color = hexToRgb(hexColor) ?? [0, 0, 0];
  // Stroke is black for most markers.
  const stroke = new Stroke({
    color: "#000000",
    width: 2.0,
  });

  // color.concat adds a transparency value and returns a new array.
  return {
    [MARKER_TYPES.normal]: new Style({
      fill: new Fill({
        color: color.concat([0.4]),
      }),
      stroke: stroke,
    }),
    [MARKER_TYPES.muted]: new Style({
      fill: new Fill({
        color: color.concat([0.2]),
      }),
      stroke: stroke,
    }),
    [MARKER_TYPES.selected]: new Style({
      fill: new Fill({
        color: color.concat([0.6]),
      }),
      stroke: stroke,
    }),
    [MARKER_TYPES.hover]: new Style({
      fill: new Fill({
        color: color.concat([0.6]),
      }),
      stroke: new Stroke({
        color: hexColor,
        width: 2.0,
      }),
    }),
  };
};

interface OlStyleMap {
  [FALLBACK_COLOR]: Record<MARKER_TYPES, Style>;
  [hex: string]: Record<MARKER_TYPES, Style>;
}

const getOlPointStyleMap = () =>
  COLORS.reduce((colorMap, color) => {
    colorMap[color] = getStyles(color);
    return colorMap;
  }, {} as OlStyleMap);

const getOlPolygonStyleMap = () =>
  COLORS.reduce((colorMap, color) => {
    colorMap[color] = getPolygonStyles(color);
    return colorMap;
  }, {} as OlStyleMap);

// NOTE(Quentin): Do not use this for anything cryptographic, we just need to convert a string into
// a random integer.
function stringToInt(inputString: string) {
  let hash = 0;
  for (let i = 0; i < inputString.length; i++) {
    const char = inputString.charCodeAt(i);
    hash = (hash << 5) - hash + char;
  }
  return hash;
}

export function featureStrToColor(featureStr: string) {
  // Use the last character of the featureStr to index into Color list
  const str_int = Math.abs(stringToInt(featureStr));
  // Get the color index using the lenght of COLORS. Remove the last element, FALLBACK_COLOR
  const color_index: number = str_int % (COLORS.length - 1);
  return COLORS[color_index];
}

export const useOlPointStyleMap = () => useMemo(getOlPointStyleMap, []);
export const useOlPolygonStyleMap = () => useMemo(getOlPolygonStyleMap, []);
